/**
 * DashAdmin — the "manager wall." Every setting that could hurt service if a
 * disgruntled staffer touched it lives (or is linked to) behind this page's
 * PIN gate. Line staff can still do everything else — this is *just* the
 * sensitive lever room.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { useManagerPinGate } from "../components/ManagerPinGate";
import ManagerPinSection from "../components/ManagerPinSection";

const SensitiveLink = ({ to, title, desc, testid }) => (
  <Link
    to={to}
    className="paper-card p-5 flex items-center justify-between gap-4 hover:border-[#D96C5B] border-2 border-transparent transition-colors"
    data-testid={testid}
  >
    <div>
      <div className="font-heading font-extrabold text-base text-[#121914]">{title}</div>
      <div className="text-xs text-[#5C6B61] mt-1 leading-snug">{desc}</div>
    </div>
    <ArrowRight size={18} color="#D96C5B" weight="bold" />
  </Link>
);

export default function DashAdmin() {
  const { business } = useAuth();
  const gate = useManagerPinGate();
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);

  // On mount: if a PIN is set, require unlock. If not, no wall.
  useEffect(() => {
    if (gate.pinSet === null) return; // still loading
    if (gate.pinSet === false) { setUnlocked(true); setChecking(false); return; }
    (async () => {
      const ok = await gate.request("open the manager control room");
      setUnlocked(ok);
      setChecking(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.pinSet]);

  if (checking) return <div className="min-h-screen bg-[#F7F6F2]">{gate.modal}</div>;

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center p-6" data-testid="admin-locked">
        {gate.modal}
        <div className="paper-card p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-[#D96C5B] mx-auto flex items-center justify-center mb-4">
            <Lock size={26} color="#fff" weight="fill" />
          </div>
          <h1 className="text-2xl font-heading font-extrabold mb-2">Manager control room</h1>
          <p className="text-sm text-[#5C6B61] mb-5 leading-relaxed">
            This is where the sensitive levers live — turning the AI off, changing manager phones, toggling paid features. Enter the manager PIN to unlock.
          </p>
          <button
            type="button"
            onClick={async () => { const ok = await gate.request("open the manager control room"); setUnlocked(ok); }}
            className="btn-primary"
            data-testid="admin-unlock-btn"
          >
            Unlock
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2] py-10 px-6">
      {gate.modal}
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[#1E3A2F] flex items-center justify-center">
            <ShieldCheck size={22} color="#F5C543" weight="fill" />
          </div>
          <div className="overline text-[#D96C5B]">Manager control room · unlocked for 5 min</div>
        </div>
        <h1 className="text-3xl font-heading font-extrabold tracking-tight mb-2">Sensitive levers</h1>
        <p className="text-sm text-[#5C6B61] max-w-2xl leading-relaxed mb-8">
          Every setting on this page can affect service if changed. Line staff who don't know the PIN can't get in here. Change what you need, then close the tab or navigate away — the room auto-locks after 5 minutes of inactivity.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <SensitiveLink
            to="/dashboard/voice"
            title="AI Receptionist on / off"
            desc="Switch calls between the AI and a live-forward number. Turning OFF is what staff would sabotage during a slow shift."
            testid="admin-link-ai"
          />
          <SensitiveLink
            to="/dashboard/settings"
            title="Manager phone & schedule"
            desc="Where callbacks and transfers land. A rogue employee could reroute the whole phone tree to their own number."
            testid="admin-link-manager"
          />
          <SensitiveLink
            to="/dashboard/waitlist"
            title="Waitlist open / closed"
            desc="Closing the waitlist mid-shift shuts off walk-ins. Set to 'auto' for capacity-based, 'open' or 'closed' for manual."
            testid="admin-link-waitlist"
          />
          <SensitiveLink
            to="/dashboard/voice"
            title="Twilio credentials"
            desc="SID, auth token, and phone number for outbound calls & SMS. Never share these — anyone with them can send SMS on your dime."
            testid="admin-link-twilio"
          />
          <SensitiveLink
            to="/dashboard/settings"
            title="Business hours & AI greeting"
            desc="What the AI says and when it says the restaurant is closed. Small edits here can change every caller's first impression."
            testid="admin-link-greeting"
          />
          <SensitiveLink
            to="/pricing"
            title="Paid features & billing"
            desc="Add or cancel paid add-ons, update payment method, view invoices. Only the owner should touch these."
            testid="admin-link-billing"
          />
        </div>

        {/* The PIN itself lives on this page too — you don't want staff resetting it from Settings */}
        <div className="paper-card p-6">
          <ManagerPinSection />
        </div>

        <div className="mt-8 text-xs text-[#5C6B61] leading-relaxed max-w-2xl">
          <b className="text-[#121914]">How it works.</b> When you set a manager PIN, this room requires it. Every unsafe toggle across the app also checks the same unlock — so if you've entered the PIN in the last 5 minutes, you won't be re-prompted while walking through settings. Once you close the tab or the 5 minutes lapse, everything re-locks. Every successful unlock is logged for later audit.
        </div>
      </div>
    </div>
  );
}
